import CloseIcon from "@mui/icons-material/Close";
import { Box, Drawer, FormControl, FormControlLabel, IconButton, InputLabel, Select } from "@mui/material";
import { ReactComponent as ArrowDown } from "assets/ArrowDown.svg";
import { ReactComponent as ArrowUp } from "assets/ArrowUp.svg";
import { ReactComponent as Filter } from "assets/filter.svg";
import { setFiltersApplied, setSelectedFilters } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import { cloneDeep, union } from "lodash";
import * as React from "react";
import { useContext } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { getListOfFilters } from "utils/CommonFunctions";
import Button from "../Button";
import CheckBox from "../Checkbox";
import CollapsibleContainer from "../CollapsibleContainer";
import Typography from "../Typography";

export const TYPES = {
  Select: "SELECT",
  Drawer: "DRAWER"
};

const MenuProps = {
  PaperProps: {
    sx: {
      border: `1px solid ${tokens.Border.General.Tertiary}`,
      boxShadow: "0px 10px 25px rgba(100, 116, 139, 0.1), 0px 20px 40px 8px rgba(100, 116, 139, 0.3)",
      borderRadius: "8px",
      maxHeight: "300px",
      ".MuiList-root": {
        paddingY: "0"
      }
    }
  }
};

const SelectStyles = {
  height: "44px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: tokens.Background.General.Tertiary,
  color: tokens.Text.General.Secondary,
  fontWeight: 700,
  fontSize: "14px",
  boxShadow: "none",
  borderRadius: "8px",
  ".MuiSelect-icon": { alignSelf: "center", top: "45%" },
  border: `2px solid ${tokens.Border.General.Tertiary}`,
  ".MuiOutlinedInput-notchedOutline": {
    border: "none"
  },
  "&:hover": {
    backgroundColor: tokens.Background.General.Secondary,
    border: `2px solid ${tokens.Border.General.Tertiary}`
  },
  "&.Mui-focused": {
    border: `inherit`
  }
};
interface Props {
  type?: string | boolean;
  openDrawer: any;
  filters: any;
  setOpenDrawer: (val: boolean) => void;
}
export default function FilterCategories(props: Props) {
  const dispatch = useAppDispatch();

  const { openDrawer, filters, setOpenDrawer, type = TYPES.Select } = props;
  const [expanded, setExpanded] = React.useState<string[]>([]);
  const [checkedList, setCheckedList] = React.useState<any>([]);
  const selectedFilters = useAppSelector((state: any) => state.app.selectedFilters);
  const filterIds = useAppSelector((state: any) => state.app.filterIds);
  const { isMobileScreen } = useContext(ThemeContext);

  React.useEffect(() => {
    if (!selectedFilters.length) {
      setCheckedList(
        filters.map((item: any) => {
          return {
            id: item.id,
            childrenCount: item.filters.length,
            checked: []
          };
        })
      );
    } else {
      setCheckedList([...selectedFilters]);
    }
  }, [filters, selectedFilters]);

  const toggleInput = React.useCallback(
    (open: any) => (event: React.KeyboardEvent | React.MouseEvent | React.SyntheticEvent<Element, Event>) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }
      setOpenDrawer(open);
    },
    [setOpenDrawer]
  );

  const handleChange = (panel: string) => {
    let newArr = [...expanded];
    const position = expanded.indexOf(panel);
    if (position > -1) newArr.splice(position, 1);
    setExpanded(position > -1 ? newArr : [...expanded, panel]);
  };

  const hoverEffect =
    type === TYPES.Drawer
      ? {}
      : {
          "&:hover": {
            backgroundColor: tokens.Background.General.Color,
            color: tokens.Text.General.Color
          }
        };

  const getParentCheckBoxes = () => {
    return filters.map((item: any) => {
      const childrenCount = checkedList.find((ele: any) => ele.id === item.id)?.childrenCount;
      const checkedLength = checkedList.find((ele: any) => ele.id === item.id)?.checked?.length;
      const isInderminate = checkedLength > 0 && checkedLength !== childrenCount;
      return (
        <React.Fragment key={item.id}>
          <Box
            sx={{
              ...{
                display: "flex",
                justifyContent: "space-between",
                padding: type === TYPES.Drawer ? "10px 25px" : "0 25px",
                color: tokens.Text.General.PrimaryHeadline
              },
              ...hoverEffect
            }}
          >
            <FormControlLabel
              label={
                <Typography variant={type === TYPES.Drawer ? "body1" : "body2"} showBold>
                  {item.name}
                </Typography>
              }
              control={
                <CheckBox
                  id={item.id}
                  checked={childrenCount > 0 && checkedLength === childrenCount}
                  indeterminate={isInderminate}
                  onChange={handleParentChange}
                />
              }
            />
            <IconButton aria-label="expand" onClick={() => handleChange(item.id)}>
              {expanded.indexOf(item.id) > -1 ? <ArrowUp /> : <ArrowDown />}
            </IconButton>
          </Box>

          <CollapsibleContainer isOpen={expanded.indexOf(item.id) > -1}>
            {getChildrenCheckboxes(item)}
          </CollapsibleContainer>
        </React.Fragment>
      );
    });
  };

  const getChildrenCheckboxes = (parent: any) => {
    return (
      <Box>
        {parent.filters.map((subFilter: any) => {
          const isChecked = checkedList.length
            ? checkedList.find((checkedList: any) => checkedList.id === parent.id)?.checked.indexOf(subFilter.id) > -1
            : false;

          return (
            <Box
              key={subFilter.id}
              sx={{
                ...{
                  paddingLeft: "50px",
                  color: tokens.Text.General.PrimaryHeadline
                },
                ...hoverEffect
              }}
            >
              <FormControlLabel
                label={<Typography variant={type === TYPES.Drawer ? "body1" : "body2"}>{subFilter.name}</Typography>}
                control={
                  <CheckBox
                    id={subFilter.id}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChildrenChange(e, parent.id)}
                    checked={isChecked}
                  />
                }
              />
            </Box>
          );
        })}
      </Box>
    );
  };

  const handleParentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = event.target;
    const selectedChildren = filters.find((item: any) => item.id === id).filters.map((ele: any) => ele.id);

    const newCheckedList = cloneDeep(checkedList);
    const currentItem = newCheckedList.find((item: any) => item.id === id);

    if (checked) {
      currentItem.checked = union(currentItem.checked, selectedChildren);
    } else {
      currentItem.checked.splice(0, currentItem.checked.length);
    }
    setCheckedList(newCheckedList);
  };

  const handleChildrenChange = (event: React.ChangeEvent<HTMLInputElement>, parentId: string) => {
    const { id, checked } = event.target;
    const newCheckedList = cloneDeep(checkedList);
    if (checked) {
      newCheckedList.find((item: any) => item.id === parentId).checked.push(id);
    } else {
      const position = newCheckedList.find((item: any) => item.id === parentId).checked.indexOf(id);
      newCheckedList.find((item: any) => item.id === parentId).checked.splice(position, 1);
    }
    setCheckedList(newCheckedList);
  };

  const getOptions = () => (
    <Box
      key={"options"}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%"
      }}
    >
      <Box minWidth={"350px"} maxWidth={"470px"} role="presentation" onKeyDown={toggleInput(false)}>
        {type === TYPES.Drawer ? (
          <Box padding={"25px 25px 0"} display={"flex"} alignItems="center" marginBottom={2}>
            <Typography variant={"h5"} showBold color={tokens.Text.General.Secondary}>
              Filters ({checkedList.map((item: any) => item.checked).flat().length} selected)
            </Typography>
            <Box marginLeft={"auto"}>
              <IconButton onClick={toggleInput(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Box padding={"25px 25px 0"} display={"flex"} alignItems="center" marginBottom={2}>
            <Typography variant={"body1"} showBold color={tokens.Text.General.PrimaryHeadline}>
              Filter by category
            </Typography>
          </Box>
        )}
        <Box>{getParentCheckBoxes()}</Box>
      </Box>
      {getFooter()}
    </Box>
  );

  const getFooter = () => {
    return (
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-evenly",
          borderTop: "1px solid #E7EAEE",
          padding: "24px 0px 24px 0px",
          background: "white",
          boxShadow: "0px 0px 4px rgba(19, 41, 138, 0.1), 0px 10px 25px rgba(19, 41, 138, 0.1)"
        }}
      >
        <Button
          variant="secondary"
          label="Clear all"
          onClick={() => {
            setCheckedList(
              filters.map((item: any) => {
                return {
                  id: item.id,
                  childrenCount: item.filters.length,
                  checked: []
                };
              })
            );
            if (getListOfFilters().length === 1) dispatch(setFiltersApplied(false));
          }}
        />
        <Button
          variant="primary"
          label="Apply"
          onClick={() => {
            dispatch(setSelectedFilters(checkedList));
            const checkedItems = checkedList.map((item: any) => item.checked).flat();
            if (checkedItems.length === 0 && getListOfFilters().length === 0) dispatch(setFiltersApplied(false));
            setOpenDrawer(false);
          }}
        />
      </Box>
    );
  };

  const getDrawerLayout = () => {
    return (
      <>
        <Button
          onClick={toggleInput(TYPES.Drawer)}
          startIcon={<Filter />}
          endIcon={
            filterIds.length > 0 && (
              <Box>
                <Typography
                  sx={{
                    backgroundColor: tokens.Background.General.LightColor,
                    paddingX: "8px",
                    borderRadius: "200px"
                  }}
                  className="count-badge"
                  variant={"body2"}
                  showBold
                >
                  {filterIds.length}
                </Typography>
              </Box>
            )
          }
          variant="primary"
          label={filterIds.length > 0 && isMobileScreen ? `Categories` : `Filter Categories`}
        />
        <Drawer
          anchor={"right"}
          open={openDrawer === TYPES.Drawer}
          onClose={toggleInput(false)}
          PaperProps={{
            sx: {
              justifyContent: "space-between",
              width: { xs: "100%", md: "auto" }
            }
          }}
        >
          {getOptions()}
        </Drawer>
      </>
    );
  };

  const getDropdownLayout = () => {
    return (
      <FormControl
        sx={{
          minWidth: "160px",
          "& legend": {
            display: "none"
          }
        }}
      >
        <InputLabel sx={{ top: "-4px" }} shrink={false} id="label">
          <Typography component="span" variant={"body2"} color={tokens.Text.General.Secondary} showBold>
            {`Filter category (${filterIds.length})`}
          </Typography>
        </InputLabel>
        <Select
          sx={SelectStyles}
          MenuProps={MenuProps}
          IconComponent={ArrowDown}
          onClose={toggleInput(false)}
          onOpen={toggleInput(TYPES.Select)}
          open={openDrawer === TYPES.Select}
        >
          {getOptions()}
        </Select>
      </FormControl>
    );
  };

  return type === TYPES.Drawer ? getDrawerLayout() : getDropdownLayout();
}
